/**
 * A streaming Markdown → ANSI renderer for the terminal.
 *
 * The model streams Markdown (`**bold**`, `# headings`, `- lists`, fenced code,
 * `[links](…)`, …). Raw, that shows literal asterisks and no styling. This module
 * turns each COMPLETE line into styled ANSI as it arrives, so the answer renders
 * live and looks alive instead of a wall of plain text with stray `**`.
 *
 * Design: line-oriented + stateful for the one construct that spans lines —
 * fenced code blocks. Everything else (headings, lists, quotes, rules, inline
 * emphasis/code/links) resolves within a single line, so it can be styled the
 * moment the line completes. All colour goes through the TTY-gated {@link color}
 * palette, so off a terminal the markers are simply stripped (clean plain text).
 */
import { color } from './ui.js';

/** A placeholder delimiter (private-use area) that never occurs in real text. */
const H0 = String.fromCharCode(0xe000);
const H1 = String.fromCharCode(0xe001);

const ESC = String.fromCharCode(27);
/** Strip SGR codes — used to flatten inline styling inside a heading (which is
 *  itself uniformly styled, so a nested inline reset would clobber its colour). */
const stripAnsi = (s: string): string => s.replace(new RegExp(`${ESC}\\[[0-9;]*m`, 'g'), '');
/** Above this length a line is emitted WITHOUT inline styling: the inline passes
 *  are ~O(n²) on pathological single lines, and no real answer line is this long. */
const MAX_INLINE = 8000;
/** Italic `_x_`, allowing a stashed code-span sentinel as the left boundary so
 *  `` `code`_word_ `` still emphasises (symmetric with the `*` rule). */
const UNDERSCORE_ITALIC = new RegExp(`(^|[\\s([{${H0}${H1}])_(?!\\s)([^_\\n]+?)_(?![\\w])`, 'g');

/**
 * Style the INLINE markdown inside one line's text: code spans, links, images,
 * bold, italic, bold-italic, strikethrough. Code spans are extracted first so no
 * other rule touches their contents. Heuristic (not full CommonMark) but robust
 * for the emphasis/code/link markup models actually emit.
 */
export function styleInline(text: string): string {
  // Cheap length guard: the inline regexes are polynomial on adversarial input
  // (e.g. thousands of unterminated "![a](" ), which would freeze the event loop.
  if (!text || text.length > MAX_INLINE) return text;
  const spans: string[] = [];
  const stash = (styled: string): string => `${H0}${spans.push(styled) - 1}${H1}`;

  // 1. Inline code `x` — literal contents, highest precedence.
  let s = text.replace(/`([^`\n]+)`/g, (_m, c: string) => stash(color.cyan(c)));

  // 2. Images ![alt](url) → dim alt, before links so the '!' isn't left behind.
  s = s.replace(/!\[([^\]]*)\]\([^)\n]*\)/g, (_m, alt: string) => color.dim(alt || 'image'));
  // 3. Links [text](url) → underlined cyan text + a dim url.
  s = s.replace(
    /\[([^\]\n]+)\]\((\S+?)\)/g,
    (_m, t: string, u: string) => stash(`${color.underline(color.cyan(t))}${color.dim(` (${u})`)}`),
  );

  // 4. Emphasis — widest markers first so ** isn't half-eaten by *.
  s = s.replace(/\*\*\*([^*\n]+?)\*\*\*/g, (_m, t: string) => color.bold(color.italic(t)));
  s = s.replace(/\*\*([^*\n]+?)\*\*/g, (_m, t: string) => color.bold(t));
  s = s.replace(/__([^_\n]+?)__/g, (_m, t: string) => color.bold(t));
  // Italic: a lone * not adjacent to another * (so leftover ** never matches),
  // and a lone _ only at a word boundary (so snake_case is left alone).
  s = s.replace(/(^|[^*\s])?\*(?!\s)([^*\n]+?)\*(?!\*)/g, (m, p: string | undefined, t: string) =>
    p === undefined ? color.italic(t) : `${p}${color.italic(t)}`,
  );
  s = s.replace(UNDERSCORE_ITALIC, (_m, p: string, t: string) => `${p}${color.italic(t)}`);
  // 5. Strikethrough ~~x~~
  s = s.replace(/~~([^~\n]+?)~~/g, (_m, t: string) => color.strike(t));

  // 6. Restore the stashed code/link spans.
  return s.replace(new RegExp(`${H0}(\\d+)${H1}`, 'g'), (_m, i: string) => spans[Number(i)] ?? '');
}

/** Heading styles by level — a coloured accent bar + bold, dimming as it deepens. */
function heading(level: number, text: string): string {
  // Flatten inline markup to plain text first: the heading is wrapped in a single
  // colour+bold, and a nested inline reset (ESC[0m) would drop that on the rest of
  // the line. Resolving markers to plain text keeps the whole heading uniform.
  const body = stripAnsi(styleInline(text));
  switch (level) {
    case 1:
      return color.bold(color.magenta(`▌ ${body}`));
    case 2:
      return color.bold(color.cyan(`▌ ${body}`));
    case 3:
      return color.bold(color.yellow(body));
    default:
      return color.bold(color.dim(body));
  }
}

/**
 * Stateful, line-at-a-time renderer. Feed it each finished line via {@link line};
 * call {@link reset} at a turn boundary so an unclosed code fence can't leak into
 * the next answer.
 */
export class MarkdownStream {
  private inFence = false;
  private fenceChar = ''; // '`' or '~' of the OPENING fence
  private fenceLen = 0; //   length of the opening fence run

  reset(): void {
    this.inFence = false;
    this.fenceChar = '';
    this.fenceLen = 0;
  }

  /** Whether the renderer is currently inside a fenced code block. */
  get inCodeBlock(): boolean {
    return this.inFence;
  }

  /** Render one COMPLETE line (no trailing newline) to styled ANSI. */
  line(raw0: string): string {
    const raw = raw0.replace(/\r$/, ''); // CRLF: a trailing CR would defeat the block regexes below

    // Inside a fenced code block: contents are LITERAL. Close only on a bare fence
    // of the SAME family and >= length — so `~~~` can't close a ``` block, and a
    // code line that merely begins with a fence char doesn't end the block early.
    if (this.inFence) {
      const close = /^\s{0,3}(`{3,}|~{3,})\s*$/.exec(raw);
      if (close && (close[1] ?? ' ')[0] === this.fenceChar && (close[1] ?? '').length >= this.fenceLen) {
        this.inFence = false;
        return color.dim('  ╰────');
      }
      return `${color.dim('  │')} ${raw}`;
    }

    // Opening a fenced code block: 3+ backticks/tildes + an optional info string.
    const open = /^\s{0,3}(`{3,}|~{3,})(.*)$/.exec(raw);
    if (open) {
      const mark = open[1] ?? '```';
      this.inFence = true;
      this.fenceChar = mark[0] ?? '`';
      this.fenceLen = mark.length;
      const lang = (open[2] ?? '').trim();
      return color.dim(`  ╭──${lang ? ` ${lang} ` : '─'}${'─'.repeat(Math.max(2, 8 - lang.length))}`);
    }

    // Headings: #, ##, … up to ######.
    const h = /^(#{1,6})\s+(.*)$/.exec(raw);
    if (h) return heading((h[1] ?? '').length, h[2] ?? '');

    // Horizontal rule: ---, ***, ___ (3+).
    if (/^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/.test(raw)) return color.dim('─'.repeat(24));

    // Blockquote: > text — a dim bar, then normal inline-styled text (dimming the
    // whole thing would be undone by any inline style's own reset).
    const bq = /^\s*>\s?(.*)$/.exec(raw);
    if (bq) return `${color.dim('┃')} ${styleInline(bq[1] ?? '')}`;

    // Unordered list: -, *, + → a coloured bullet, indentation preserved.
    const ul = /^(\s*)[-*+]\s+(.*)$/.exec(raw);
    if (ul) return `${ul[1] ?? ''}${color.cyan('•')} ${styleInline(ul[2] ?? '')}`;

    // Ordered list: 1. / 1) → keep the number, coloured.
    const ol = /^(\s*)(\d+)[.)]\s+(.*)$/.exec(raw);
    if (ol) return `${ol[1] ?? ''}${color.cyan(`${ol[2]}.`)} ${styleInline(ol[3] ?? '')}`;

    // Plain paragraph line.
    return styleInline(raw);
  }
}
